/**
 * UI-кит English Learning (холст «UI-кит», Components.dc.html).
 * Все экраны собираются из этих компонентов; цвета — только через --ds-* токены.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';
import './ui.css';

export { Icon, ICONS } from './Icon';
export { BrandMark } from './BrandMark';
export * from './format';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* ---------- Кнопки ---------- */

export const Spinner = ({ size = 14 }) => (
  <svg className="ui-spinner" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" strokeWidth="3" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" className="ui-spinner-track" />
    <path d="M21 12a9 9 0 0 0-9-9" className="ui-spinner-head" />
  </svg>
);

/**
 * variant: primary | secondary | soft | ghost | danger; size: md (44) | sm (36) | lg (48).
 * С `to` рендерится как <Link>, с `href` — как <a>.
 */
export const Button = React.forwardRef(({
  variant = 'secondary', size = 'md', icon, iconRight, loading = false, block = false,
  to, href, className, children, type = 'button', disabled, ...rest
}, ref) => {
  const classes = cx('ui-btn', `ui-btn-${variant}`, `ui-btn-${size}`, block && 'ui-btn-block', className);
  const iconSize = size === 'sm' ? 14 : 16;
  const content = (
    <>
      {loading ? <Spinner size={iconSize} /> : icon && <Icon name={icon} size={iconSize} strokeWidth={2.2} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} strokeWidth={2.2} />}
    </>
  );
  if (to) return <Link ref={ref} to={to} className={classes} {...rest}>{content}</Link>;
  if (href) return <a ref={ref} href={href} className={classes} {...rest}>{content}</a>;
  return (
    <button ref={ref} type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {content}
    </button>
  );
});

export const IconButton = React.forwardRef(({ icon, label, size = 'md', variant = 'outline', className, iconSize, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    title={label}
    className={cx('ui-icon-btn', `ui-icon-btn-${size}`, `ui-icon-btn-${variant}`, className)}
    {...rest}
  >
    <Icon name={icon} size={iconSize || (size === 'sm' ? 16 : 18)} strokeWidth={2} />
  </button>
));

/* ---------- Поля ---------- */

/** Обёртка поля: подпись, подсказка и ошибка связаны с контролом через aria-*. */
export const Field = ({ label, hint, error, children, className, id: idProp, required }) => {
  const auto = useId();
  const id = idProp || `f${auto.replace(/:/g, '')}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;
  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
      id: children.props.id || id,
      'aria-describedby': describedBy,
      'aria-invalid': error ? true : children.props['aria-invalid'],
      required: required ?? children.props.required
    })
    : children;
  return (
    <div className={cx('ui-field', className)}>
      {label && <label htmlFor={id} className="ui-label">{label}</label>}
      {control}
      {error && (
        <span id={errId} className="ui-field-error">
          <Icon name="alert" size={14} strokeWidth={2.2} />{error}
        </span>
      )}
      {hint && !error && <span id={hintId} className="ui-field-hint">{hint}</span>}
    </div>
  );
};

export const Input = React.forwardRef(({ className, size = 'md', ...rest }, ref) => (
  <input ref={ref} className={cx('ui-input', size === 'sm' && 'ui-input-sm', className)} {...rest} />
));

export const Textarea = React.forwardRef(({ className, ...rest }, ref) => (
  <textarea ref={ref} className={cx('ui-input ui-textarea', className)} {...rest} />
));

export const Select = React.forwardRef(({ className, children, size = 'md', ...rest }, ref) => (
  <span className={cx('ui-select-wrap', size === 'sm' && 'ui-select-sm', className)}>
    <select ref={ref} className="ui-input ui-select" {...rest}>{children}</select>
    <Icon name="chevronDown" size={14} strokeWidth={2} className="ui-select-chevron" />
  </span>
));

/** Поле с иконкой поиска слева. */
export const SearchInput = React.forwardRef(({ className, ...rest }, ref) => (
  <span className={cx('ui-search', className)}>
    <Icon name="search" size={16} strokeWidth={2} className="ui-search-icon" />
    <input ref={ref} type="search" className="ui-input" {...rest} />
  </span>
));

/* ---------- Выбор ---------- */

export const Checkbox = ({ checked, indeterminate = false, onChange, label, className, disabled, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return (
    <label className={cx('ui-check', disabled && 'is-disabled', className)}>
      <input ref={ref} type="checkbox" checked={!!checked} onChange={onChange} disabled={disabled} {...rest} />
      <span className="ui-check-box" aria-hidden="true">
        <Icon name={indeterminate ? 'minus' : 'check'} size={13} strokeWidth={3} />
      </span>
      {label && <span className="ui-check-label">{label}</span>}
    </label>
  );
};

export const Radio = ({ checked, onChange, label, name, value, className, disabled, hint }) => (
  <label className={cx('ui-radio', disabled && 'is-disabled', className)}>
    <input type="radio" name={name} value={value} checked={!!checked} onChange={onChange} disabled={disabled} />
    <span className="ui-radio-dot" aria-hidden="true" />
    <span className="ui-check-label">
      {label}
      {hint && <span className="ui-check-hint">{hint}</span>}
    </span>
  </label>
);

export const Switch = ({ checked, onChange, label, className, disabled, hint }) => (
  <label className={cx('ui-switch', disabled && 'is-disabled', className)}>
    <input type="checkbox" role="switch" checked={!!checked} aria-checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} disabled={disabled} />
    <span className="ui-switch-track" aria-hidden="true"><span className="ui-switch-thumb" /></span>
    {(label || hint) && (
      <span className="ui-check-label">
        {label}
        {hint && <span className="ui-check-hint">{hint}</span>}
      </span>
    )}
  </label>
);

/* ---------- Чипы и статусы ---------- */

export const STATUS_LABELS = { new: 'Новое', studying: 'Изучаю', learned: 'Выучено' };

/** Статус слова/фразы: new | studying | learned (legacy 'learning' = studying). */
export const StatusChip = ({ status = 'new', children, className }) => {
  const st = status === 'learning' ? 'studying' : (STATUS_LABELS[status] ? status : 'new');
  return (
    <span className={cx('ui-status', `ui-status-${st}`, className)}>
      <span className="ui-status-dot" aria-hidden="true" />
      {children || STATUS_LABELS[st]}
    </span>
  );
};

export const StatusDot = ({ status = 'new', size = 10 }) => (
  <span className={cx('ui-status-square', `ui-status-${status}`)} style={{ width: size, height: size }} aria-hidden="true" />
);

/** Плашка «НОВОЕ» и подобные. */
export const Tag = ({ children, tone = 'dark', className }) => (
  <span className={cx('ui-tag', `ui-tag-${tone}`, className)}>{children}</span>
);

/** Счётчик-бейдж (24 в навигации, 8 у заголовка). tone: accent | muted | danger */
export const Badge = ({ children, tone = 'accent', className }) => (
  <span className={cx('ui-badge', `ui-badge-${tone}`, className)}>{children}</span>
);

/** Фильтр-чип. `onRemove` добавляет крестик. tone: dark (Все) | accent (Глаголы). */
export const Chip = ({ pressed = false, onClick, onRemove, children, tone = 'dark', count, className, ...rest }) => (
  <button
    type="button"
    aria-pressed={pressed}
    onClick={pressed && onRemove ? onRemove : onClick}
    className={cx('ui-chip', pressed && `ui-chip-on-${tone}`, className)}
    {...rest}
  >
    {children}
    {count != null && <span className="ui-chip-count">{count}</span>}
    {pressed && onRemove && <Icon name="close" size={14} strokeWidth={2.4} />}
  </button>
);

/** Освоение: n из total сегментов. */
export const Mastery = ({ value = 0, total = 4, showLabel = true, className }) => (
  <span className={cx('ui-mastery', className)} role="img" aria-label={`Освоение ${value} из ${total}`}>
    {Array.from({ length: total }, (_, i) => (
      <span key={i} className={i < value ? 'on' : ''} />
    ))}
    {showLabel && <span className="ui-mastery-label">{value}/{total}</span>}
  </span>
);

/* ---------- Табы и сегменты ---------- */

/** Подчёркнутые вкладки. items: [{id, label, count}] */
export const Tabs = ({ items, value, onChange, label, className, idPrefix = 'tab' }) => {
  const refs = useRef({});
  const onKey = (e, i) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = items[(i + (e.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length];
    onChange(next.id);
    refs.current[next.id]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={cx('ui-tabs', className)}>
      {items.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => { refs.current[t.id] = el; }}
          type="button"
          role="tab"
          id={`${idPrefix}-${t.id}`}
          aria-selected={value === t.id}
          tabIndex={value === t.id ? 0 : -1}
          className={cx('ui-tab', value === t.id && 'is-active')}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.label}
          {t.count != null && <span className="ui-tab-count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
};

/** Сегментный переключатель (Неделя / Месяц / Всё время). role: tab | radio */
export const Segmented = ({ items, value, onChange, label, size = 'md', role = 'radio', className }) => (
  <div role={role === 'tab' ? 'tablist' : 'radiogroup'} aria-label={label} className={cx('ui-segmented', `ui-segmented-${size}`, className)}>
    {items.map((t) => (
      <button
        key={t.id}
        type="button"
        role={role}
        aria-checked={role === 'radio' ? value === t.id : undefined}
        aria-selected={role === 'tab' ? value === t.id : undefined}
        className={cx(value === t.id && 'is-active')}
        onClick={() => onChange(t.id)}
      >
        {t.label}
      </button>
    ))}
  </div>
);

/** Плотная группа кнопок (7д / 30д / 90д). */
export const ToggleGroup = ({ items, value, onChange, label, className }) => (
  <div role="group" aria-label={label} className={cx('ui-toggle-group', className)}>
    {items.map((t) => (
      <button key={t.id} type="button" aria-pressed={value === t.id} className={cx(value === t.id && 'is-active')} onClick={() => onChange(t.id)}>
        {t.label}
      </button>
    ))}
  </div>
);

/* ---------- Прогресс ---------- */

export const ProgressBar = ({ value = 0, max = 100, label, size = 8, tone = 'accent', className }) => {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div
      className={cx('ui-progress', `ui-progress-${tone}`, className)}
      style={{ height: size }}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
    >
      <div style={{ width: `${pct}%` }} />
    </div>
  );
};

/** Полоса статусов new / studying / learned. */
export const StatusBar = ({ counts = {}, size = 8, className }) => {
  const n = counts.new || 0;
  const s = counts.studying || 0;
  const l = counts.learned || 0;
  const total = n + s + l;
  const aria = `Новое ${n}, изучаю ${s}, выучено ${l}`;
  return (
    <div className={cx('ui-statusbar', className)} style={{ height: size }} role="img" aria-label={aria}>
      {total === 0 ? <div className="ui-statusbar-empty" /> : (
        <>
          {n > 0 && <div className="ui-statusbar-new" style={{ width: `${(n / total) * 100}%` }} />}
          {s > 0 && <div className="ui-statusbar-studying" style={{ width: `${(s / total) * 100}%` }} />}
          {l > 0 && <div className="ui-statusbar-learned" style={{ width: `${(l / total) * 100}%` }} />}
        </>
      )}
    </div>
  );
};

/** Кольцо прогресса (цель дня). */
export const Ring = ({ value = 0, max = 1, size = 120, stroke = 12, label, unit, ariaLabel }) => {
  const r = 50;
  const c = 2 * Math.PI * r;
  const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <svg className="ui-ring" width={size} height={size} viewBox="0 0 120 120" role="img" aria-label={ariaLabel}>
      <circle cx="60" cy="60" r={r} className="ui-ring-track" strokeWidth={stroke} />
      {ratio > 0 && (
        <circle cx="60" cy="60" r={r} className="ui-ring-fill" strokeWidth={stroke} strokeDasharray={`${c * ratio} ${c}`} transform="rotate(-90 60 60)" />
      )}
      <text x="60" y={unit ? 58 : 68} textAnchor="middle" className="ui-ring-value">{label ?? value}</text>
      {unit && <text x="60" y="80" textAnchor="middle" className="ui-ring-unit">{unit}</text>}
    </svg>
  );
};

/* ---------- Карточки, пустые состояния, загрузка ---------- */

export const Card = ({ as: Tag2 = 'section', className, padding = 'md', children, ...rest }) => (
  <Tag2 className={cx('ui-card', `ui-card-p-${padding}`, className)} {...rest}>{children}</Tag2>
);

export const SectionLabel = ({ children, className }) => (
  <div className={cx('ui-section-label', className)}>{children}</div>
);

export const EmptyState = ({ icon = 'book', title, text, action, className }) => (
  <div className={cx('ui-empty', className)}>
    <span className="ui-empty-icon"><Icon name={icon} size={22} strokeWidth={1.9} /></span>
    {title && <span className="ui-empty-title">{title}</span>}
    {text && <span className="ui-empty-text">{text}</span>}
    {action && <div className="ui-empty-action">{action}</div>}
  </div>
);

export const Skeleton = ({ width = '100%', height = 10, radius = 4, className }) => (
  <span className={cx('ui-skeleton', className)} style={{ width, height, borderRadius: radius }} aria-hidden="true" />
);

export const SkeletonList = ({ rows = 3 }) => (
  <div className="ui-skeleton-list" aria-busy="true" aria-label="Загрузка">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="ui-skeleton-row">
        <Skeleton width={36} height={36} radius={999} />
        <div>
          <Skeleton width={`${[70, 55, 80][i % 3]}%`} />
          <Skeleton width={`${[45, 35, 50][i % 3]}%`} className="ui-skeleton-soft" />
        </div>
      </div>
    ))}
  </div>
);

export const Alert = ({ tone = 'error', children, action, className }) => (
  <div role={tone === 'error' ? 'alert' : 'status'} className={cx('ui-alert', `ui-alert-${tone}`, className)}>
    <Icon name={tone === 'error' ? 'alert' : 'info'} size={18} strokeWidth={2.2} />
    <span className="ui-alert-text">{children}</span>
    {action}
  </div>
);

/* ---------- Аватары ---------- */

const AVATAR_TONES = ['accent', 'blue', 'amber', 'purple', 'pink'];

/** Аватар: с `src` — фото в круге (object-fit: cover); без src или при ошибке загрузки — инициал. */
export const Avatar = ({ name = '?', size = 36, tone, className, src }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  const initial = String(name).trim().charAt(0).toUpperCase() || '?';
  const t = tone || AVATAR_TONES[(initial.charCodeAt(0) || 0) % AVATAR_TONES.length];
  const showPhoto = !!src && !failed;
  return (
    <span
      className={cx('ui-avatar', showPhoto ? 'ui-avatar-photo' : `ui-avatar-${t}`, className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden="true"
    >
      {showPhoto
        ? <img src={src} alt="" width={size} height={size} draggable="false" onError={() => setFailed(true)} />
        : initial}
    </span>
  );
};

/* ---------- Модальные окна ---------- */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useDialogBehavior(open, onClose, ref) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    const node = ref.current;
    const first = node?.querySelector('[data-autofocus]') || node?.querySelector(FOCUSABLE);
    (first || node)?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      if (prev && prev.focus) prev.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

/**
 * Диалог: role=dialog, Esc, ловушка фокуса, клик по фону закрывает.
 * size: sm (420) | md (560) | lg (720) | xl (880). На телефоне — нижний лист.
 */
export const Modal = ({
  open, onClose, title, description, children, footer, size = 'md', className,
  closeOnBackdrop = true, hideClose = false, headerExtra
}) => {
  const ref = useRef(null);
  const titleId = useId();
  useDialogBehavior(open, onClose, ref);
  if (!open) return null;
  return createPortal(
    <div className="ui-modal-backdrop" onMouseDown={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose?.(); }}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cx('ui-modal', `ui-modal-${size}`, className)}
      >
        {(title || !hideClose) && (
          <div className="ui-modal-head">
            <div className="ui-modal-titles">
              {title && <h2 id={titleId} className="ui-modal-title">{title}</h2>}
              {description && <p className="ui-modal-desc">{description}</p>}
            </div>
            {headerExtra}
            {!hideClose && <IconButton icon="close" label="Закрыть" variant="ghost" onClick={onClose} />}
          </div>
        )}
        <div className="ui-modal-body">{children}</div>
        {footer && <div className="ui-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

/** Подтверждение (удалить урок, удалить слова). */
export const ConfirmDialog = ({
  open, onClose, onConfirm, title, children, confirmLabel = 'Удалить', cancelLabel = 'Отмена',
  danger = true, loading = false
}) => (
  <Modal
    open={open}
    onClose={onClose}
    title={title}
    size="sm"
    hideClose
    footer={(
      <>
        <Button onClick={onClose}>{cancelLabel}</Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading} data-autofocus>
          {confirmLabel}
        </Button>
      </>
    )}
  >
    <div className="ui-confirm-text">{children}</div>
  </Modal>
);

/** Боковая панель справа (на телефоне — нижний лист). */
export const SidePanel = ({ open, onClose, title, children, footer, width = 440, className, headerExtra }) => {
  const ref = useRef(null);
  const titleId = useId();
  useDialogBehavior(open, onClose, ref);
  if (!open) return null;
  return createPortal(
    <div className="ui-panel-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx('ui-panel', className)}
        style={{ '--ui-panel-w': `${width}px` }}
      >
        <div className="ui-panel-head">
          <h2 id={titleId} className="ui-panel-title">{title}</h2>
          {headerExtra}
          <IconButton icon="close" label="Закрыть" variant="ghost" onClick={onClose} />
        </div>
        <div className="ui-panel-body">{children}</div>
        {footer && <div className="ui-panel-foot">{footer}</div>}
      </aside>
    </div>,
    document.body
  );
};

/* ---------- Меню ---------- */

/**
 * Выпадающее меню. trigger — функция ({ref, props}) или используется кнопка «⋯».
 * items: [{label, icon, onClick, danger, divider, hint}]
 */
export const Menu = ({ items, label = 'Ещё', align = 'right', triggerClassName, renderTrigger }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 240;
    let left = align === 'right' ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const below = window.innerHeight - r.bottom;
    const top = below < 280 ? Math.max(8, r.top - 8) : r.bottom + 6;
    setPos({ left, top, up: below < 280 });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const close = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const els = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
        const i = els.indexOf(document.activeElement);
        const next = e.key === 'ArrowDown' ? (i + 1) % els.length : (i - 1 + els.length) % els.length;
        els[next]?.focus();
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (open && pos) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open, pos]);

  const triggerProps = {
    ref: btnRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (e) => { e.stopPropagation(); setOpen((v) => !v); }
  };

  return (
    <>
      {renderTrigger ? renderTrigger(triggerProps) : (
        <button type="button" aria-label={label} title={label} className={cx('ui-icon-btn ui-icon-btn-sm ui-icon-btn-outline', triggerClassName)} {...triggerProps}>
          <Icon name="more" size={18} strokeWidth={3} />
        </button>
      )}
      {open && pos && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className={cx('ui-menu', pos.up && 'is-up')}
          style={{ left: pos.left, top: pos.top }}
        >
          {items.filter(Boolean).map((it, i) => (it.divider ? <div key={i} className="ui-menu-divider" role="separator" /> : (
            <button
              key={i}
              type="button"
              role="menuitem"
              tabIndex={-1}
              disabled={it.disabled}
              className={cx('ui-menu-item', it.danger && 'is-danger')}
              onClick={() => { setOpen(false); it.onClick?.(); }}
            >
              {it.icon && <Icon name={it.icon} size={16} />}
              <span>{it.label}</span>
              {it.hint && <span className="ui-menu-hint">{it.hint}</span>}
            </button>
          )))}
        </div>,
        document.body
      )}
    </>
  );
};

/* ---------- Тосты ---------- */

const ToastContext = createContext(null);

/**
 * toast.show({ text, tone: 'success'|'error'|'info', action: {label, onClick}, undo: fn, duration })
 * Undo-тост показывает обратный отсчёт «Отменить · 5».
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearInterval(timers.current[id]);
    delete timers.current[id];
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((opts) => {
    const id = Math.random().toString(36).slice(2);
    const duration = opts.duration ?? (opts.undo ? 5 : 4);
    setToasts((list) => [...list.slice(-2), { id, tone: 'success', ...opts, left: duration }]);
    timers.current[id] = setInterval(() => {
      setToasts((list) => {
        const t = list.find((x) => x.id === id);
        if (!t) return list;
        if (t.left <= 1) {
          clearInterval(timers.current[id]);
          if (t.onExpire) t.onExpire();
          return list.filter((x) => x.id !== id);
        }
        return list.map((x) => (x.id === id ? { ...x, left: x.left - 1 } : x));
      });
    }, 1000);
    return id;
  }, []);

  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);

  const api = useMemo(() => ({
    show,
    dismiss,
    success: (text, o) => show({ text, tone: 'success', ...o }),
    error: (text, o) => show({ text, tone: 'error', duration: 6, ...o }),
    info: (text, o) => show({ text, tone: 'info', ...o })
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="ui-toasts" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'} className={cx('ui-toast', `ui-toast-${t.tone}`)}>
              <Icon name={t.tone === 'error' ? 'alert' : t.undo ? 'trash' : t.tone === 'info' ? 'info' : 'check'} size={18} strokeWidth={2.2} />
              <span className="ui-toast-text">{t.text}</span>
              {t.undo && (
                <button type="button" className="ui-toast-action" onClick={() => { t.undo(); dismiss(t.id); }}>
                  Отменить · {t.left}
                </button>
              )}
              {t.action && (
                <button type="button" className="ui-toast-action" onClick={() => { t.action.onClick(); dismiss(t.id); }}>
                  {t.action.label}
                </button>
              )}
              {!t.undo && (
                <button type="button" className="ui-toast-close" aria-label="Закрыть уведомление" onClick={() => dismiss(t.id)}>
                  <Icon name="close" size={14} strokeWidth={2.2} />
                </button>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

const NOOP_TOAST = { show: () => {}, dismiss: () => {}, success: () => {}, error: () => {}, info: () => {} };
export const useToast = () => useContext(ToastContext) || NOOP_TOAST;

/* ---------- Заголовок страницы ---------- */

export const PageHeader = ({ title, subtitle, actions, className, children }) => (
  <header className={cx('ui-page-head', className)}>
    <div className="ui-page-titles">
      <h1 className="ui-page-title">{title}</h1>
      {subtitle && <p className="ui-page-subtitle">{subtitle}</p>}
      {children}
    </div>
    {actions && <div className="ui-page-actions">{actions}</div>}
  </header>
);

/** Шаги визарда: 1 — 2 — 3. */
export const Stepper = ({ steps, current, className }) => (
  <ol className={cx('ui-stepper', className)}>
    {steps.map((s, i) => (
      <li key={s} className={cx(i < current && 'is-done', i === current && 'is-current')} aria-current={i === current ? 'step' : undefined}>
        <span className="ui-stepper-num">{i < current ? <Icon name="check" size={12} strokeWidth={3} /> : i + 1}</span>
        <span className="ui-stepper-label">{s}</span>
      </li>
    ))}
  </ol>
);
